import { LANGUAGE_CONFIG } from "./constants";

/**
 * UI copy keyed by preferred-language code (`LANGUAGE_CONFIG.OPTIONS`).
 * Office-only copy (impersonation) stays in the component.
 * Free-form user text should go through a translation API, not this catalog.
 */
export const UI_COPY = {
  nav: {
    back: { en: "Back", es: "Atrás" },
    noPreviousPage: { en: "No previous page", es: "No hay página anterior" },
    returnHome: { en: "Return to Homepage", es: "Volver al inicio" },
    userMenu: { en: "User menu", es: "Menú de usuario" },
    closeMenu: { en: "Close menu", es: "Cerrar menú" },
    language: { en: "Preferred Language", es: "Idioma Preferido" },
    languageUpdateFailed: {
      en: "Unable to update language. / No se pudo actualizar el idioma.",
      es: "No se pudo actualizar el idioma. / Unable to update language.",
    },
    openAdminPanel: { en: "Open Admin Panel", es: "Abrir panel de administración" },
    logout: { en: "Logout", es: "Cerrar sesión" },
    logoutTitle: { en: "Log Out?", es: "¿Cerrar sesión?" },
    logoutConfirmAccount: {
      en: "Are you sure you want to log out of the account {email}?",
      es: "¿Seguro que quieres cerrar la sesión de {email}?",
    },
    logoutConfirmGeneric: {
      en: "Are you sure you want to log out of this account?",
      es: "¿Seguro que quieres cerrar la sesión de esta cuenta?",
    },
    logoutConfirm: { en: "Log Out", es: "Cerrar sesión" },
    cancel: { en: "Cancel", es: "Cancelar" },
  },
  home: {
    crew: { en: "Crew", es: "Cuadrilla" },
    modules: { en: "Modules:", es: "Módulos:" },
    apps: {
      "clock-in": { en: "Clock In", es: "Registrar entrada" },
      "worker-schedule": { en: "Shift Schedule", es: "Horario de turnos" },
      dispatch: { en: "Daily Dispatch", es: "Asignación diaria" },
      drive: { en: "R&S Shared Drive", es: "Unidad compartida R&S" },
      upload: { en: "Media Upload", es: "Carga de medios" },
    },
  },
  newUser: {
    title: { en: "Language Selection", es: "Selección de idioma" },
    languageHelp: {
      en: "The R&S App is now available to use in both Spanish and English, please select your preferred language:",
      es: "La aplicación R&S ahora está disponible en español e inglés. Selecciona tu idioma preferido:",
    },
    continue: { en: "Continue to App", es: "Continuar a la app" },
    processing: { en: "Processing...", es: "Procesando..." },
    completeError: {
      en: "Unable to save. Please try again.",
      es: "No se pudo guardar. Inténtalo de nuevo.",
    },
  },
  sms: {
    title: { en: "Contact Permission", es: "Permiso de contacto" },
    cellPhoneNumber: { en: "Cell Phone Number", es: "Número de celular" },
    numberFormat: {
      en: "Number format should be 123-456-7890",
      es: "El formato debe ser 123-456-7890",
    },
    permissionLabel: {
      en: "I give permission to receive shift scheduling SMS messages from R&S Construction App",
      es: "Doy permiso para recibir mensajes SMS de programación de turnos de R&S Construction App",
    },
    permissionHelp: {
      en: "By checking this box, you agree to receive shift scheduling SMS messages from the R&S Construction App, which inform you as soon as possible where and when you will be working. You can opt out at any time by replying STOP or unchecking the box in the app. Msg & data rates may apply.",
      es: "Al marcar esta casilla, aceptas recibir mensajes SMS de programación de turnos de R&S Construction App, que te informan lo antes posible dónde y cuándo trabajarás. Puedes cancelar en cualquier momento respondiendo STOP o desmarcando la casilla en la app. Pueden aplicar tarifas de mensajes y datos.",
    },
    termsLink: {
      en: "Click to see Privacy Policy and Terms (new tab)",
      es: "Haz clic para ver la Política de privacidad y los Términos (nueva pestaña)",
    },
    permissionRequired: {
      en: "Please check the box to give permission",
      es: "Marca la casilla para dar permiso",
    },
    reject: { en: "REJECT Permission", es: "RECHAZAR permiso" },
    confirm: { en: "Confirm Permission", es: "Confirmar permiso" },
    processing: { en: "Processing...", es: "Procesando..." },
    successSet: {
      en: "SMS Permissions Successfully Set",
      es: "Permisos SMS configurados correctamente",
    },
    saveError: {
      en: "Unable to save SMS preferences. Please try again.",
      es: "No se pudieron guardar las preferencias de SMS. Inténtalo de nuevo.",
    },
  },
  password: {
    title: { en: "Update Your Password", es: "Actualiza tu contraseña" },
    subtitle: {
      en: "For security, we recommend updating your password to a personal one.",
      es: "Por seguridad, te recomendamos actualizar tu contraseña a una personal.",
    },
    currentPassword: { en: "Current Password", es: "Contraseña actual" },
    currentPasswordPlaceholder: {
      en: "Enter current password",
      es: "Ingresa la contraseña actual",
    },
    newPassword: { en: "New Password", es: "Contraseña nueva" },
    newPasswordPlaceholder: {
      en: "Enter new password",
      es: "Ingresa la contraseña nueva",
    },
    confirmPassword: { en: "Confirm New Password", es: "Confirmar contraseña nueva" },
    confirmPasswordPlaceholder: {
      en: "Confirm new password",
      es: "Confirma la contraseña nueva",
    },
    minLengthHint: {
      en: "Must be at least 8 characters",
      es: "Debe tener al menos 8 caracteres",
    },
    noThanks: { en: "No, thanks", es: "No, gracias" },
    updatePassword: { en: "Update Password", es: "Actualizar contraseña" },
    processing: { en: "Processing...", es: "Procesando..." },
    currentRequired: {
      en: "Current password is required",
      es: "Se requiere la contraseña actual",
    },
    newRequired: {
      en: "New password is required",
      es: "Se requiere la contraseña nueva",
    },
    minLengthError: {
      en: "Password must be at least 8 characters",
      es: "La contraseña debe tener al menos 8 caracteres",
    },
    confirmRequired: {
      en: "Please confirm your new password",
      es: "Confirma tu contraseña nueva",
    },
    mismatch: {
      en: "Passwords do not match",
      es: "Las contraseñas no coinciden",
    },
    genericError: {
      en: "An error occurred. Please try again.",
      es: "Ocurrió un error. Inténtalo de nuevo.",
    },
    errors: {
      old_password_incorrect: {
        en: "Current password is incorrect",
        es: "La contraseña actual es incorrecta",
      },
      password_mismatch: {
        en: "Passwords do not match",
        es: "Las contraseñas no coinciden",
      },
      password_too_short: {
        en: "Password must be at least 8 characters",
        es: "La contraseña debe tener al menos 8 caracteres",
      },
      password_too_common: {
        en: "This password is too common",
        es: "Esta contraseña es demasiado común",
      },
      password_entirely_numeric: {
        en: "This password is entirely numeric",
        es: "Esta contraseña es solo números",
      },
      password_too_similar: {
        en: "This password is too similar to your personal information",
        es: "Esta contraseña es demasiado similar a tu información personal",
      },
      password_invalid: {
        en: "This password is not valid",
        es: "Esta contraseña no es válida",
      },
    },
  },
  clockIn: {
    pageTitle: { en: "R&S Clock In", es: "R&S Registrar entrada" },
    title: { en: "Clock In", es: "Registrar entrada" },
    job: { en: "Job", es: "Trabajo" },
    selectJob: { en: "Select a Job", es: "Selecciona un trabajo" },
    noJobs: { en: "No jobs available", es: "No hay trabajos disponibles" },
    workScope: { en: "Work Scope", es: "Alcance de trabajo" },
    selectScope: { en: "Select a Scope", es: "Selecciona un alcance" },
    noScopes: {
      en: "No scopes, please contact your super.",
      es: "No hay alcances, contacta a tu supervisor.",
    },
    workDescription: { en: "Work Description", es: "Descripción del trabajo" },
    workDescriptionHint: {
      en: "(You can enter English or Spanish)",
      es: "(Puedes escribir en inglés o español)",
    },
    workDescriptionPlaceholder: {
      en: "Describe your work activities here...",
      es: "Describe aquí tus actividades de trabajo...",
    },
    workDescriptionTranslation: {
      en: "Translation preview",
      es: "Vista previa de la traducción",
    },
    workDescriptionTranslationUnavailable: {
      en: "Translation unavailable. You can still clock in.",
      es: "No se pudo traducir. Aún puedes registrar la entrada.",
    },
    timeIn: { en: "Time In", es: "Hora de entrada" },
    submit: { en: "Clock In", es: "Registrar entrada" },
    viewSchedule: { en: "View Shift Schedule", es: "Ver horario de turnos" },
    addAnotherShift: { en: "Add Another Shift", es: "Agregar otro turno" },
    cancelClockIn: { en: "Cancel Clock In", es: "Cancelar entrada" },
    alreadyClockedIn: {
      en: "You are already clocked in for {date}. Please clock out before starting a new shift today.",
      es: "Ya registraste entrada para el {date}. Por favor registra la salida antes de iniciar un nuevo turno hoy.",
    },
    lookingForSchedule: {
      en: "Looking for work schedule for {date}",
      es: "Buscando horario de trabajo para el {date}",
    },
    scheduleLoaded: {
      en: "Work schedule loaded for {date}",
      es: "Horario de trabajo cargado para el {date}",
    },
    additionalShiftsNotFound: {
      en: "Additional scheduled shifts not found for {date}. Please enter manually.",
      es: "No se encontraron turnos programados adicionales para el {date}. Por favor ingresa los datos manualmente.",
    },
    scheduleNotFound: {
      en: "Work schedule not found for {date}. Please enter manually.",
      es: "No se encontró horario de trabajo para el {date}. Por favor ingresa los datos manualmente.",
    },
    fillAllFields: {
      en: "Please fill in all fields",
      es: "Por favor completa todos los campos",
    },
    timeInBeforePrevious: {
      en: "Time in cannot be before previous shift's finish time",
      es: "La hora de entrada no puede ser anterior a la hora de salida del turno anterior",
    },
    clockInSuccessful: {
      en: "Clock in successful",
      es: "Entrada registrada",
    },
    clockInFailed: { en: "Clock in failed", es: "Error al registrar entrada" },
    clockInError: {
      en: "An error occurred while clocking in: {error}",
      es: "Ocurrió un error al registrar entrada: {error}",
    },
    draftNote: {
      en: "Note: This shift is not yet confirmed.",
      es: "Nota: Este turno aún no está confirmado.",
    },
    unknownJob: { en: "Unknown Job", es: "Trabajo desconocido" },
    unknownScope: { en: "Unknown Scope", es: "Alcance desconocido" },
    yes: { en: "Yes", es: "Sí" },
    no: { en: "No", es: "No" },
    stages: {
      "Clocked In": { en: "Clocked In", es: "Entrada registrada" },
      "Current Shift": { en: "Current Shift", es: "Turno actual" },
      "Complete Shift": { en: "Complete Shift", es: "Turno completo" },
      "Shift (Draft)": { en: "Shift (Draft)", es: "Turno (borrador)" },
      Shift: { en: "Shift", es: "Turno" },
      "Missed Shift": { en: "Missed Shift", es: "Turno perdido" },
      "No Shift": { en: "No Shift", es: "Sin turno" },
      "No Shift Data": { en: "No Shift Data", es: "Sin datos de turno" },
      "New Shift": { en: "New Shift", es: "Nuevo turno" },
    },
    fields: {
      job: { en: "Job", es: "Trabajo" },
      jobNum: { en: "Job Number", es: "Número de trabajo" },
      JobDescription: { en: "Job Description", es: "Descripción del trabajo" },
      workScope: { en: "Work Scope", es: "Alcance de trabajo" },
      workDescription: { en: "Work Description", es: "Descripción del trabajo" },
      startTime: { en: "Start Time", es: "Hora de inicio" },
      finishTime: { en: "Finish Time", es: "Hora de salida" },
      regularTime: { en: "Regular Time", es: "Tiempo regular" },
      overtime: { en: "Overtime", es: "Tiempo extra" },
      doubleTime: { en: "Double Time", es: "Tiempo doble" },
      breaksCompleted: { en: "Breaks Completed", es: "Descansos completados" },
      totalHours: { en: "Total Hours", es: "Horas totales" },
      regularHours: { en: "Regular Hours", es: "Horas regulares" },
      overtimeHours: { en: "Overtime Hours", es: "Horas extra" },
      notes: { en: "Notes", es: "Notas" },
      address: { en: "Address", es: "Dirección" },
    },
    clockOut: {
      title: { en: "Clock Out", es: "Registrar salida" },
      timeOut: { en: "Time Out", es: "Hora de salida" },
      submit: { en: "Clock Out", es: "Registrar salida" },
      enterTime: { en: "Please enter a time", es: "Por favor ingresa una hora" },
      beforeClockIn: {
        en: "Clock out time cannot be before clock in time",
        es: "La hora de salida no puede ser anterior a la hora de entrada",
      },
      clockOutSuccessful: {
        en: "Clock Out successful",
        es: "Salida registrada",
      },
      clockOutFailed: { en: "Clock out failed", es: "Error al registrar salida" },
      clockOutError: {
        en: "An error occurred while clocking out",
        es: "Ocurrió un error al registrar la salida",
      },
      breakTitle: { en: "Break Confirmation", es: "Confirmación de descansos" },
      breakPrompt: {
        en: "Did you take all legally required breaks during this shift?",
        es: "¿Tomaste todos los descansos requeridos por ley durante este turno?",
      },
      breakYes: { en: "Yes, I did", es: "Sí, los tomé" },
      breakNo: { en: "No, I didn't", es: "No, no los tomé" },
    },
  },
  schedule: {
    pageTitle: { en: "R&S Shift Schedule", es: "R&S Horario de turnos" },
    timesheet: { en: "Timesheet:", es: "Hoja de tiempo:" },
    thisWeek: { en: "This Week", es: "Esta semana" },
    lastWeek: { en: "Last Week", es: "Semana pasada" },
    weeksAgo: { en: "{count} Weeks Ago", es: "Hace {count} semanas" },
    weeksAhead: { en: "{count} Weeks Ahead", es: "{count} semanas adelante" },
    today: { en: "(Today)", es: "(Hoy)" },
    editShift: { en: "Edit Shift", es: "Editar turno" },
    editShifts: { en: "Edit Shifts", es: "Editar turnos" },
    viewToday: { en: "View Today", es: "Ver hoy" },
    returnToWeeklySchedule: {
      en: "Return to Weekly Schedule",
      es: "Volver al horario semanal",
    },
    weeklyHoursTotals: {
      en: "Weekly Hours Totals",
      es: "Totales de horas semanales",
    },
    hours: { en: "Hours:", es: "Horas:" },
    regular: { en: "Regular:", es: "Regular:" },
    overtime: { en: "Overtime:", es: "Tiempo extra:" },
    doubleTime: { en: "Double Time:", es: "Tiempo doble:" },
    overtimeAbbr: { en: "OT", es: "extra" },
    doubleTimeAbbr: { en: "DT", es: "doble" },
    noLongerEditable: {
      en: "Shifts for this week are no longer editable.",
      es: "Los turnos de esta semana ya no se pueden editar.",
    },
    noLongerEditableDeadline: {
      en: "Shifts for this week are no longer editable. Payroll deadline is Tuesday.",
      es: "Los turnos de esta semana ya no se pueden editar. La fecha límite de nómina es el martes.",
    },
    daysRemaining: {
      en: "{count} days remaining to submit timesheet.",
      es: "Quedan {count} días para enviar la hoja de tiempo.",
    },
    oneDayRemaining: {
      en: "1 day remaining to submit timesheet.",
      es: "Queda 1 día para enviar la hoja de tiempo.",
    },
    lastDayToSubmit: {
      en: "Last day to submit timesheet.",
      es: "Último día para enviar la hoja de tiempo.",
    },
    timesheetSubmitted: {
      en: "Timesheet submitted, thank you.",
      es: "Hoja de tiempo enviada, gracias.",
    },
    reminderAvailable: {
      en: "Weekly timesheet now available for {week}",
      es: "La hoja de tiempo semanal ya está disponible para {week}",
    },
    reminderConfirm: {
      en: "I have completed all my shifts for {week}.",
      es: "Completé todos mis turnos de {week}.",
    },
    thisWeekPhrase: { en: "this week", es: "esta semana" },
    lastWeekPhrase: { en: "last week", es: "la semana pasada" },
    signWeeklyTimesheet: {
      en: "Sign Weekly Timesheet",
      es: "Firmar hoja de tiempo semanal",
    },
    edit: {
      title: { en: "Shift", es: "Turno" },
      breaksTaken: {
        en: "Legally required breaks taken",
        es: "Descansos requeridos por ley tomados",
      },
      saveShift: { en: "Save Shift", es: "Guardar turno" },
      noShiftData: {
        en: "No shift data provided.",
        es: "No hay datos de turno.",
      },
      fillRequiredFields: {
        en: "Please fill in all required fields",
        es: "Por favor completa todos los campos requeridos",
      },
      startBeforeFinish: {
        en: "Start time must be before finish time",
        es: "La hora de entrada debe ser anterior a la hora de salida",
      },
      startAfterPrevious: {
        en: "Start time must be after the previous shift's finish time",
        es: "La hora de entrada debe ser posterior a la hora de salida del turno anterior",
      },
      finishBeforeNext: {
        en: "Finish time must be before the next shift's start time",
        es: "La hora de salida debe ser anterior a la hora de entrada del siguiente turno",
      },
      shiftSaved: { en: "Shift saved successfully", es: "Turno guardado" },
      shiftUpdated: {
        en: "Shift updated successfully",
        es: "Turno actualizado",
      },
      saveFailed: { en: "Save shift failed", es: "Error al guardar el turno" },
      editFailed: { en: "Edit shift failed", es: "Error al editar el turno" },
      updateFailed: {
        en: "Failed to update shift. Please try again.",
        es: "No se pudo actualizar el turno. Inténtalo de nuevo.",
      },
    },
  },
  timesheet: {
    pageTitle: { en: "Submit Timesheet", es: "Enviar hoja de tiempo" },
    checkDetails: {
      en: "Please check your timesheet details below:",
      es: "Revisa los detalles de tu hoja de tiempo:",
    },
    name: { en: "Name", es: "Nombre" },
    weekEnding: { en: "Week Ending", es: "Semana al" },
    notAvailable: { en: "N/A", es: "N/D" },
    signature: { en: "Signature", es: "Firma" },
    clear: { en: "Clear", es: "Borrar" },
    submit: { en: "Submit Timesheet", es: "Enviar hoja de tiempo" },
    submitted: { en: "Submitted", es: "Enviada" },
    signatureRequired: {
      en: "Please provide your signature before submitting",
      es: "Por favor firma antes de enviar",
    },
    signatureNotReady: {
      en: "Signature component not ready",
      es: "La firma no está lista",
    },
    successTitle: {
      en: "Timesheet Submitted Successfully!",
      es: "¡Hoja de tiempo enviada!",
    },
    successManagerNotified: {
      en: "Thank you, your manager has been notified and will review your timesheet.",
      es: "Gracias, tu supervisor ha sido notificado y revisará tu hoja de tiempo.",
    },
    successReturnHint: {
      en: "You will be returned to your weekly schedule when you press OK.",
      es: "Volverás a tu horario semanal al presionar Aceptar.",
    },
    ok: { en: "OK", es: "Aceptar" },
    loading: {
      en: "Loading shift data...",
      es: "Cargando datos de turnos...",
    },
    workDetails: { en: "Work Details", es: "Detalles del trabajo" },
    hours: { en: "Hours", es: "Horas" },
    day: { en: "Day", es: "Día" },
    job: { en: "Job", es: "Trabajo" },
    timeIn: { en: "In", es: "Entrada" },
    timeOut: { en: "Out", es: "Salida" },
    total: { en: "Total", es: "Total" },
    editHours: { en: "Edit Hours", es: "Editar horas" },
    saveHours: { en: "Save Hours", es: "Guardar horas" },
    deleteShift: { en: "Delete shift", es: "Eliminar turno" },
    swipeToDelete: {
      en: "Swipe a shift to delete",
      es: "Desliza un turno para eliminarlo",
    },
    hoverToDelete: {
      en: "Hover over the day to delete shift",
      es: "Pasa el cursor sobre el día para eliminar el turno",
    },
    hoursProvisional: {
      en: "All hours are provisional until confirmed by a manager",
      es: "Todas las horas son provisionales hasta que un supervisor las confirme",
    },
    reviewSafetyMeeting: {
      en: "Review Safety Meeting",
      es: "Revisar reunión de seguridad",
    },
    certifyHours: {
      en: "I certify that the hours recorded on this timesheet are true and complete, and that all time I worked is accurately recorded. I received all required meal and rest breaks, or I promptly reported any issues to my Supervisor. I have reported any work-related injuries or illnesses.",
      es: "Certifico que las horas registradas en esta hoja de tiempo son verdaderas y están completas, y que todo el tiempo que trabajé ha sido registrado correctamente. Recibí todos los períodos de comida y descanso requeridos, o informé de inmediato a mi Supervisor sobre cualquier inconveniente. He reportado cualquier lesión o enfermedad relacionada con el trabajo.",
    },
    safetyMeetingAttestation: {
      en: "Pressing confirm attests to and verifies my understanding of and agreement to comply with, all company safety policies and regulations, and confirms that I have not suffered, experienced, or sustained any recent job-related injury or illness. I also confirm I attended the weekly site safety meeting and my signature will be used to record this.",
      es: "Al presionar «Confirmar», declaro y certifico que comprendo y acepto cumplir con todas las políticas y normas de seguridad de la empresa, y confirmo que no he sufrido, presentado ni padecido recientemente ninguna lesión o enfermedad relacionada con el trabajo. Asimismo, confirmo que asistí a la reunión semanal de seguridad en el lugar de trabajo y autorizo el uso de mi firma para dejar constancia de mi asistencia.",
    },
    weeklySafetyMeeting: {
      en: "Weekly Safety Meeting",
      es: "Reunión semanal de seguridad",
    },
    confirmReviewed: {
      en: "Confirm Reviewed",
      es: "Confirmar",
    },
    checklist: {
      clockIn: {
        en: "Please clock in for your shifts before submitting your timesheet.",
        es: "Registra la entrada de tus turnos antes de enviar la hoja de tiempo.",
      },
      saveChanges: {
        en: "Please save your changes before submitting.",
        es: "Guarda los cambios antes de enviar.",
      },
      safetyMeeting: {
        en: "Please review the safety meeting before submitting.",
        es: "Revisa la reunión de seguridad antes de enviar.",
      },
      sign: {
        en: "Please sign your timesheet before submitting.",
        es: "Firma tu hoja de tiempo antes de enviar.",
      },
      allMet: {
        en: "All requirements met! You can submit your timesheet.",
        es: "Se cumplieron todos los requisitos. Ya puedes enviar tu hoja de tiempo.",
      },
    },
    delete: {
      title: { en: "Delete Shift?", es: "¿Eliminar turno?" },
      cannotUndo: {
        en: "This action cannot be undone.",
        es: "Esta acción no se puede deshacer.",
      },
      clockInAgain: {
        en: "You will need to clock in again to record a new shift.",
        es: "Tendrás que registrar la entrada de nuevo para anotar un turno nuevo.",
      },
      date: { en: "Date:", es: "Fecha:" },
      job: { en: "Job:", es: "Trabajo:" },
      time: { en: "Time:", es: "Hora:" },
      confirm: { en: "Delete Shift", es: "Eliminar turno" },
    },
  },
  login: {
    returnHome: { en: "Return to Homepage", es: "Volver al inicio" },
    heading: { en: "Sign in to your account", es: "Inicia sesión en tu cuenta" },
    language: { en: "Language", es: "Idioma" },
    footerBrand: { en: "R&S Construction", es: "R&S Construction" },
    footerApp: { en: "Operations App", es: "App de operaciones" },
    errors: {
      invalid: {
        en: "The email or password you entered was not recognized.",
        es: "El correo o la contraseña no fueron reconocidos.",
      },
      inactive: {
        en: "This account has been marked inactive. Please contact the office.",
        es: "Esta cuenta está inactiva. Por favor contacta a la oficina.",
      },
    },
    sms: {
      badge: { en: "SMS Code Login", es: "Inicio por código SMS" },
      title: { en: "Text me a login code", es: "Envíame un código de inicio" },
      help: {
        en: "Enter the phone number on your account. We will text you a short code to complete sign in.",
        es: "Ingresa el número de teléfono de tu cuenta. Te enviaremos un código corto para iniciar sesión.",
      },
      phoneLabel: { en: "US Phone Number", es: "Número de teléfono de EE. UU." },
      phonePlaceholder: { en: "123-456-7890", es: "123-456-7890" },
      codeLabel: { en: "Login Code", es: "Código de inicio" },
      codePlaceholder: { en: "Enter code", es: "Ingresa el código" },
      differentPhone: {
        en: "Use a different phone number",
        es: "Usar otro número de teléfono",
      },
      processing: { en: "Processing...", es: "Procesando..." },
      sendCode: { en: "Send Login Code", es: "Enviar código" },
      logIn: { en: "Log In", es: "Iniciar sesión" },
      usePassword: {
        en: "Or log in via password",
        es: "O inicia sesión con contraseña",
      },
      invalidPhone: {
        en: "Number format should be 123-456-7890",
        es: "El formato debe ser 123-456-7890",
      },
      sentCode: {
        en: "If this number is registered, we sent a login code.",
        es: "Si este número está registrado, enviamos un código de inicio.",
      },
      sendFailed: {
        en: "Unable to send a login code right now.",
        es: "No se pudo enviar un código de inicio en este momento.",
      },
      codeRequired: {
        en: "Enter the login code sent to your phone.",
        es: "Ingresa el código de inicio enviado a tu teléfono.",
      },
      verifyFailed: {
        en: "Unable to verify this code.",
        es: "No se pudo verificar este código.",
      },
      codeInvalid: {
        en: "The code was invalid or expired.",
        es: "El código no es válido o expiró.",
      },
    },
    password: {
      title: { en: "Password Login", es: "Inicio con contraseña" },
      help: {
        en: "Use your company assigned email and personal password to log in.",
        es: "Usa el correo asignado por la empresa y tu contraseña personal para iniciar sesión.",
      },
      email: { en: "Email", es: "Correo electrónico" },
      emailPlaceholder: { en: "Your Email", es: "Tu correo electrónico" },
      password: { en: "Password", es: "Contraseña" },
      passwordPlaceholder: { en: "Password", es: "Contraseña" },
      logIn: { en: "Log In", es: "Iniciar sesión" },
      resetHelp: {
        en: "To reset your password, please contact the office.",
        es: "Para restablecer tu contraseña, contacta a la oficina.",
      },
      useSms: {
        en: "Or log in via phone code",
        es: "O inicia sesión con código de teléfono",
      },
    },
  },
};

/**
 * Maps a phone-login API message to language-keyed login SMS copy.
 *
 * @param {string} [message] Default-language server message from phone login.
 * @returns {Object<string, string>|null} Matching copy entry, or null.
 */
export function loginSmsErrorCopy(message) {
  const mapped = {
    "Enter a valid US phone number.": UI_COPY.login.sms.invalidPhone,
    "If this number is registered, we sent a login code.": UI_COPY.login.sms.sentCode,
    "Unable to send login code.": UI_COPY.login.sms.sendFailed,
    "Unable to send a login code right now.": UI_COPY.login.sms.sendFailed,
    "Enter the login code sent to your phone.": UI_COPY.login.sms.codeRequired,
    "Unable to verify this code.": UI_COPY.login.sms.verifyFailed,
    "The code was invalid or expired.": UI_COPY.login.sms.codeInvalid,
  };
  return mapped[message] || null;
}

/**
 * Resolve a language-keyed copy entry for the active user language.
 *
 * @param {Object<string, string>|null|undefined} copy Strings keyed by language code.
 * @param {string} [language=LANGUAGE_CONFIG.DEFAULT] Preferred language code.
 * @param {Object<string, string|number>} [replacements={}] `{name}` substitutions.
 * @returns {string} Localized string, falling back to the default language.
 */
export function uiText(copy, language = LANGUAGE_CONFIG.DEFAULT, replacements = {}) {
  const template =
    copy?.[language] || copy?.[LANGUAGE_CONFIG.DEFAULT] || "";
  return Object.entries(replacements).reduce((text, [key, value]) => {
    return text.replaceAll(`{${key}}`, String(value ?? ""));
  }, template);
}

/**
 * Resolve a password-change API error code to localized text.
 * Pair with `mapServerPasswordErrors` in `utils/auth` (login page + password modal).
 *
 * @param {string} code Backend error code (e.g. `password_too_short`).
 * @param {string} [language=LANGUAGE_CONFIG.DEFAULT] Preferred language code.
 * @returns {string} Localized message, or the generic password error.
 */
export function passwordErrorText(code, language = LANGUAGE_CONFIG.DEFAULT) {
  return uiText(
    UI_COPY.password.errors[code] || UI_COPY.password.genericError,
    language
  );
}

/**
 * Localized clock-in / shift-summary stage label.
 *
 * @param {string} [stage] Backend stage string (e.g. `Clocked In`).
 * @param {string} [language=LANGUAGE_CONFIG.DEFAULT] Preferred language code.
 * @returns {string} Localized stage, or the original string if unknown.
 */
export function clockInStageText(stage, language = LANGUAGE_CONFIG.DEFAULT) {
  if (!stage) return "";
  return uiText(UI_COPY.clockIn.stages[stage], language) || stage;
}

/**
 * Localized ShiftSummary field label.
 *
 * @param {string} key Shift object key (e.g. `workScope`).
 * @param {string} [language=LANGUAGE_CONFIG.DEFAULT] Preferred language code.
 * @returns {string} Localized label, or a spaced camelCase fallback.
 */
export function clockInFieldLabel(key, language = LANGUAGE_CONFIG.DEFAULT) {
  const copy = UI_COPY.clockIn.fields[key];
  if (copy) return uiText(copy, language);
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
